import type { WireMessage } from "./agentMemory";
import { CONVERSATION_FILE_TEXT_MARKER, readsConversationFiles } from "./conversationFiles";

export const CONDENSE_ABOVE_MESSAGES = 240;
export const CONDENSE_ABOVE_CHARS = 300_000;
export const KEEP_RECENT_MESSAGES = 120;
export const KEEP_RECENT_CHARS = 150_000;
const SUMMARY_MAX_CHARS = 40_000;
const USER_LINE_CHARS = 600;
const REPLY_LINE_CHARS = 800;
const TOOL_LINE_CHARS = 300;

// The BFF refuses a plan request over 400 messages or 500,000 characters (MAX_PLAN_MESSAGES and
// MAX_PLAN_CONTENT_CHARS in PlanAgentTurn.kt); the margin leaves room for the page context and tool definitions.
const PLAN_BUDGET_MESSAGES = 370;
const PLAN_BUDGET_CHARS = 400_000;

export const fitsPlanBudget = (messages: readonly WireMessage[]): boolean =>
  messages.length <= PLAN_BUDGET_MESSAGES && sizeOf(messages) <= PLAN_BUDGET_CHARS;

export const SUMMARY_HEADING =
  "Sammendrag av den tidligere delen av samtalen (forkortet automatisk, de siste meldingene står i sin helhet " +
  "etter dette). Bruk id-ene og beslutningene her når brukeren viser til noe fra tidligere:";

export const settleHistoryForTurn = (messages: WireMessage[]): number => {
  condenseOlderTurns(messages);
  return messages.length;
};

export const condenseOlderTurns = (messages: WireMessage[]): boolean => {
  if (messages.length <= CONDENSE_ABOVE_MESSAGES && sizeOf(messages) <= CONDENSE_ABOVE_CHARS) return false;
  const start = messages[0]?.role === "system" ? 1 : 0;
  const cut = firstKeptUserMessage(messages, start);
  if (cut <= start) return false;

  const older = messages.slice(start, cut);
  const lines = older.flatMap(digestLines);
  const keptToTheEu = readsConversationFiles(older) ? `${CONVERSATION_FILE_TEXT_MARKER}\n` : "";
  const summary = { role: "system", content: `${SUMMARY_HEADING}\n${keptToTheEu}${boundedFromTheEnd(lines)}` };
  messages.splice(start, cut - start, summary);
  return true;
};

export const sizeOf = (messages: readonly WireMessage[]): number =>
  messages.reduce(
    (sum, m) => sum + (m.content?.length ?? 0) + (m.toolCalls ?? []).reduce((n, c) => n + c.arguments.length, 0),
    0,
  );

const firstKeptUserMessage = (messages: readonly WireMessage[], start: number): number => {
  const userIndexes = messages.map((m, i) => (m.role === "user" && i > start ? i : -1)).filter((i) => i >= 0);
  const fits = userIndexes.find(
    (i) => messages.length - i <= KEEP_RECENT_MESSAGES && sizeOf(messages.slice(i)) <= KEEP_RECENT_CHARS,
  );
  return fits ?? userIndexes[userIndexes.length - 1] ?? start;
};

const digestLines = (message: WireMessage): string[] => {
  if (message.role === "system") {
    return message.content?.startsWith(SUMMARY_HEADING)
      ? message.content
          .slice(SUMMARY_HEADING.length)
          .trim()
          .split("\n")
          .filter((line) => line !== CONVERSATION_FILE_TEXT_MARKER)
      : [];
  }
  if (message.role === "user") return [`Brukeren: ${clip(message.content, USER_LINE_CHARS)}`];
  if (message.role === "tool") return [`  → ${clip(message.content, TOOL_LINE_CHARS)}`];
  const calls = (message.toolCalls ?? []).map((c) => `  Verktøy ${c.name}(${clip(c.arguments, TOOL_LINE_CHARS)})`);
  const reply = message.content?.trim() ? [`Assistenten: ${clip(message.content, REPLY_LINE_CHARS)}`] : [];
  return [...reply, ...calls];
};

const clip = (text: string | null | undefined, max: number): string => {
  const flat = (text ?? "").replace(/\s+/g, " ").trim();
  return flat.length <= max ? flat : `${flat.slice(0, max)}…`;
};

const boundedFromTheEnd = (lines: string[]): string => {
  const kept: string[] = [];
  let size = 0;
  for (let i = lines.length - 1; i >= 0 && size + lines[i].length + 1 <= SUMMARY_MAX_CHARS; i--) {
    kept.unshift(lines[i]);
    size += lines[i].length + 1;
  }
  return kept.join("\n");
};
